import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../config/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    const storedToken = localStorage.getItem('token');
    if (!storedToken) {
      setLoading(false);
      return;
    }

    // First check if we have a stored user (from admin access token login)
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        setUser(parsedUser);
        setToken(storedToken);
        setLoading(false);
        // The stored user says nothing about whether the token is still valid.
        // Verify it in the background so an expired session is signed out
        // instead of leaving every API call failing behind a logged-in UI.
        axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${storedToken}` }
        }).catch((error) => {
          if (error.response?.status === 401 && localStorage.getItem('token') === storedToken) {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            setToken(null);
            setUser(null);
          }
        });
        return;
      } catch (e) {
        // Invalid JSON, continue to /me check
        localStorage.removeItem('user');
      }
    }

    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${storedToken}` }
      });
      setUser(response.data);
      setToken(storedToken);
    } catch (error) {
      console.error('Auth check failed:', error);
      // Only clear token on 401 (unauthorized) - not on network errors or other issues
      if (error.response?.status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setToken(null);
        setUser(null);
      }
      // For other errors (network, 500, etc.) keep the token and try again later
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(userData);
    
    // Record daily login for streak tracking
    try {
      await axios.post(`${API}/gamification/record-activity`, 
        { activity_type: 'daily_login' },
        { headers: { Authorization: `Bearer ${access_token}` } }
      );
    } catch (e) {
      console.log('Streak tracking skipped:', e.message);
    }
    
    return userData;
  };

  const register = async (email, password, fullName, role = 'learner') => {
    const response = await axios.post(`${API}/auth/register`, {
      email,
      password,
      full_name: fullName,
      role
    });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  /**
   * Establish a session from an Entra SSO callback.
   *
   * routes/sso.py completes the OAuth exchange server-side and returns the
   * finished JWT in the URL fragment, so there is no session id to trade in -
   * the token IS the credential. The older flow POSTed it to /api/auth/session,
   * an endpoint that has since been removed, which is why SSO sign-in failed
   * at this step. Store the token, then load the profile with it.
   */
  const establishSsoSession = async (accessToken) => {
    localStorage.setItem('token', accessToken);
    setToken(accessToken);

    const response = await axios.get(`${API}/auth/me`, {
      headers: { Authorization: `Bearer ${accessToken}` }
    });
    const userData = response.data;
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);

    // Record daily login for streak tracking (best effort)
    try {
      await axios.post(`${API}/gamification/record-activity`,
        { activity_type: 'daily_login' },
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
    } catch (e) {
      // non-fatal
    }

    return userData;
  };

  const handleOAuthCallback = async (sessionId) => {
    const response = await axios.post(`${API}/auth/session`, { session_id: sessionId });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(userData);
    
    // Record daily login for streak tracking
    try {
      await axios.post(`${API}/gamification/record-activity`, 
        { activity_type: 'daily_login' },
        { headers: { Authorization: `Bearer ${access_token}` } }
      );
    } catch (e) {
      console.log('Streak tracking skipped:', e.message);
    }
    return userData;
  };

  const logout = async () => {
    try {
      if (token) {
        await axios.post(`${API}/auth/logout`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      setToken(null);
      setUser(null);
    }
  };

  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'org_admin';
  const isInstructor = user?.role === 'instructor' || isAdmin;

  return (
    <AuthContext.Provider value={{
      user,
      token,
      loading,
      login,
      register,
      loginWithGoogle,
      handleOAuthCallback,
      establishSsoSession,
      logout,
      isAdmin,
      isInstructor,
      isAuthenticated: !!user,
      checkAuth
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
